import React from "react";
import { Users, Instagram, MapPin, PlayCircle } from "lucide-react";
import { useLang, UI } from "../i18n";
import { ALUMNI, THEMES, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, MonoTag, CourtLines } from "../components/motifs";
import { ContactSection } from "../components/Layout";

const StarCard = ({ star, i, accent, t }) => {
  const flip = i % 2 === 1;
  return (
    <Reveal delay={0.05}>
      <article data-testid={`alumni-star-${i}`}
        className="relative overflow-hidden rounded-lg border border-white/10 grid grid-cols-1 lg:grid-cols-12"
        style={{ backgroundColor: "#0B0E16" }}>
        {/* faisceau projecteur */}
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse 55% 60% at ${flip ? "72%" : "28%"} -10%, ${accent}33, transparent 65%)` }} />
        <div aria-hidden className="absolute inset-y-0 pointer-events-none"
          style={{ [flip ? "right" : "left"]: "4%", width: "38%",
            background: `linear-gradient(to bottom, rgba(255,255,255,0.10), transparent 70%)`,
            clipPath: flip ? "polygon(58% 0, 78% 0, 100% 100%, 0 100%)" : "polygon(22% 0, 42% 0, 100% 100%, 0 100%)" }} />

        <div className={`relative min-h-[340px] lg:col-span-5 ${flip ? "lg:order-2" : ""} flex items-end justify-center pt-10`}>
          {star.img ? (
            <img src={star.img} alt={star.name}
              className="relative z-10 w-[240px] md:w-[280px] aspect-[3/4] object-cover object-top rounded-t-md"
              style={{ boxShadow: `0 -10px 80px ${accent}40` }} />
          ) : (
            <div className="relative z-10 w-[240px] md:w-[280px] aspect-[3/4] rounded-t-md flex items-center justify-center border border-white/10"
              style={{ backgroundColor: "#131A2A", boxShadow: `0 -10px 80px ${accent}40` }}>
              <span className="font-display text-7xl" style={{ color: accent }}>
                {star.name.split(" ").map((w) => w[0]).join("")}
              </span>
            </div>
          )}
          <div aria-hidden className="absolute bottom-0 inset-x-0 h-16"
            style={{ background: "linear-gradient(to top, #0B0E16, transparent)" }} />
          <span aria-hidden className="absolute top-6 font-display text-[7rem] leading-none text-white/[0.05] select-none"
            style={{ [flip ? "right" : "left"]: "1.5rem" }}>
            {String(i + 1).padStart(2, "0")}
          </span>
        </div>

        <div className={`relative z-10 lg:col-span-7 p-8 md:p-12 ${flip ? "lg:order-1" : ""}`}>
          <MonoTag style={{ color: accent }}>★ ALUMNI N° AST-{String(i + 1).padStart(3, "0")}</MonoTag>
          <h3 className="font-display text-4xl md:text-5xl uppercase leading-none mt-3">{star.name}</h3>
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] mt-3 text-white/70">
            <MapPin size={13} style={{ color: accent }} /> {t(star.current)}
          </p>
          <ol className="mt-7 space-y-0">
            {star.journey.map((s, k) => (
              <li key={k} className="relative pl-8 pb-4 last:pb-0">
                <span aria-hidden className="absolute left-[7px] top-5 bottom-0 w-px bg-white/15 last:hidden"
                  style={k === star.journey.length - 1 ? { display: "none" } : {}} />
                <span className="absolute left-0 top-1 w-[15px] h-[15px] rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: accent, backgroundColor: k === star.journey.length - 1 ? accent : "transparent" }} />
                <p className={`font-body text-sm leading-relaxed ${k === star.journey.length - 1 ? "text-white font-semibold" : "text-white/70"}`}>{t(s)}</p>
              </li>
            ))}
          </ol>
          {star.insta && (
            <a href={star.insta} target="_blank" rel="noreferrer" data-testid={`star-insta-${i}`}
              className="inline-flex items-center gap-2 mt-7 rounded-full px-6 py-3 font-heading font-semibold uppercase text-xs tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
              style={{ backgroundColor: accent }}>
              <Instagram size={15} /> {t({ fr: "Voir ses highlights", en: "Watch highlights", es: "Ver sus highlights" })}
            </a>
          )}
        </div>
      </article>
    </Reveal>
  );
};

export default function Alumni() {
  useLenis();
  const { t } = useLang();
  const th = THEMES.alumni;
  return (
    <MiniSite theme={th}>
      <PageHero
        code="AST · ALUMNI"
        overline={t(UI.nav.alumni)}
        title={t(ALUMNI.title)}
        subtitle={t(ALUMNI.intro)}
        image={IMG.dunk}
      />

      {/* STARS — MODE PROJECTEUR */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="alumni-stars">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>✦ {t(ALUMNI.starsOverline)} ✦</MonoTag>
        </Reveal>
        <div className="mt-8 space-y-8">
          {ALUMNI.stars.map((s, i) => (
            <StarCard key={s.name} star={s} i={i} accent={th.accent} t={t} />
          ))}
        </div>
      </section>

      {/* ALPHA BASKETBALL — 20+ HIGHLIGHTS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Reveal>
          <div data-testid="alumni-alpha" className="relative overflow-hidden rounded-lg border border-white/10 p-8 md:p-12 grain"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            <CourtLines className="-left-48 -bottom-48 w-[440px] h-[440px]" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <Instagram size={30} style={{ color: th.accent }} />
                <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-4">{t(ALUMNI.alpha.title)}</h2>
                <p className="font-body text-white/75 mt-4 max-w-2xl">{t(ALUMNI.alpha.desc)}</p>
              </div>
              <a href={ALUMNI.alpha.url} target="_blank" rel="noreferrer" data-testid="alumni-alpha-cta"
                className="inline-flex items-center gap-2 self-start rounded-full px-7 py-3.5 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                <Instagram size={16} /> {t(ALUMNI.alpha.cta)}
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* RETOURS VIDÉO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t(ALUMNI.reviews.overline)}</MonoTag>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {ALUMNI.reviews.list.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.1}>
              <a href={r.url} target="_blank" rel="noreferrer" data-testid={`alumni-review-${i}`}
                className="group flex items-start gap-5 h-full rounded-lg border border-white/10 p-7 hover:border-white/30 transition-colors duration-300"
                style={{ backgroundColor: "var(--ms-surface)" }}>
                <PlayCircle size={38} className="shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ color: th.accent }} />
                <span>
                  <span className="font-display text-2xl uppercase block">{r.name}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] block mt-1" style={{ color: th.accent }}>{t(r.role)}</span>
                  <span className="font-body text-sm text-white/70 block mt-3">{t(r.desc)}</span>
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-4 group-hover:text-white transition-colors duration-300">
                    <Instagram size={12} /> {t({ fr: "Voir la vidéo", en: "Watch the video", es: "Ver el vídeo" })}
                  </span>
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MENTORAT & TRANSITION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <Reveal>
          <div data-testid="alumni-mentor" className="relative overflow-hidden rounded-lg border border-white/10 p-8 md:p-12 grain"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            <CourtLines className="-right-48 -top-48 w-[480px] h-[480px]" />
            <div className="relative z-10 max-w-3xl">
              <Users size={30} style={{ color: th.accent }} />
              <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-4">{t(ALUMNI.mentor.title)}</h2>
              <p className="font-body text-white/75 mt-5">{t(ALUMNI.mentor.desc)}</p>
              <a href="#contact" data-testid="alumni-mentor-cta"
                className="inline-flex items-center gap-2 mt-7 rounded-full px-7 py-3.5 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                {t(ALUMNI.mentor.cta)}
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <ContactSection defaultProgram={t({ fr: "Alumni & Mentorat", en: "Alumni & Mentoring", es: "Alumni y Mentoría" })} />
    </MiniSite>
  );
}
